/**
 * Alert zones — axis-aligned rectangles a subscriber draws on the map to limit
 * push notifications to the areas they actually care about. They are stored
 * next to that browser's push subscription, so they are a per-device
 * preference, not a property of the pins.
 *
 * An empty list means "notify me everywhere", which is what every subscriber
 * had before this existed, so old records keep working untouched.
 *
 * Shared by the browser and the server: keep it free of browser APIs.
 */

export interface AlertZone {
  id: string
  west: number
  south: number
  east: number
  north: number
}

/** Enough for anyone in practice, and it bounds the per-push filtering work. */
export const MAX_ALERT_ZONES = 12

export function zoneContains(
  zone: AlertZone,
  lng: number,
  lat: number
): boolean {
  return (
    lng >= zone.west &&
    lng <= zone.east &&
    lat >= zone.south &&
    lat <= zone.north
  )
}

/** Should a subscriber with these zones hear about something at lng/lat? */
export function isInAnyZone(
  zones: AlertZone[] | undefined,
  lng: number,
  lat: number
): boolean {
  if (!zones || zones.length === 0) return true
  return zones.some((zone) => zoneContains(zone, lng, lat))
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

/**
 * Coerce untrusted input — a request body, or localStorage written by an older
 * build — into well-formed zones, dropping anything that isn't one.
 */
export function sanitizeZones(raw: unknown): AlertZone[] {
  if (!Array.isArray(raw)) return []

  const zones: AlertZone[] = []
  for (const candidate of raw) {
    if (!candidate || typeof candidate !== 'object') continue
    const { id, west, south, east, north } = candidate as Record<
      string,
      unknown
    >
    if (typeof id !== 'string' || !id) continue
    if (
      !isFiniteNumber(west) ||
      !isFiniteNumber(south) ||
      !isFiniteNumber(east) ||
      !isFiniteNumber(north)
    ) {
      continue
    }
    // Degenerate or inside-out boxes would silently match nothing, or (for a
    // box spanning the antimeridian) not what was drawn.
    if (west >= east || south >= north) continue
    if (west < -180 || east > 180 || south < -90 || north > 90) continue

    zones.push({ id, west, south, east, north })
    if (zones.length >= MAX_ALERT_ZONES) break
  }
  return zones
}

/**
 * Rough on-the-ground size, for labelling a zone in the UI. Equirectangular,
 * which is plenty at the scale anyone draws these.
 */
export function zoneSizeKm(zone: AlertZone): { width: number; height: number } {
  const KM_PER_DEGREE = 111.32
  const midLat = ((zone.north + zone.south) / 2) * (Math.PI / 180)
  return {
    width: (zone.east - zone.west) * KM_PER_DEGREE * Math.cos(midLat),
    height: (zone.north - zone.south) * KM_PER_DEGREE,
  }
}
